<template>
  <div v-if="authStore.currentUser" :class="[$style.bulkUpdateContent]">
    <v-card :class="[$style.bulkUpdateCard]">
      <div :class="[$style.bulkUpdateWrapper]">
        <div :class="[$style.datePickerWrapper]">
          <app-date-picker
            v-model="bulkForm.expirationDate"
            label="New Expiration Date"
            hide-details="auto"
            use-current-initial-page
            :error-messages="expirationDateErrors"
          />
        </div>
        <v-btn
          color="primary"
          :loading="isUpdating"
          :disabled="isUpdating || selectedCount === 0 || !bulkForm.expirationDate"
          @click="onBulkUpdate"
        >
          {{ globalTranslate('button.applyToSelected') }}
          <v-badge
            v-if="selectedCount > 0"
            :content="selectedCount"
            color="error"
            inline
          />
        </v-btn>
      </div>
    </v-card>

    <assigned-courses-table
      ref="assignedCoursesTableRef"
      has-filters
      is-checkbox-shown
      :headers="headers"
      :fetch-func="fetchExtendedAssignedCourses"
      :auto-save-expired-date="false"
      :is-expandable="false"
      :filters-to-shown="[TABLE_FILTERS.courses, TABLE_FILTERS.expirationStatuses]"
      :initial-filters="{ expirationStatuses: [{ name: globalTranslate('courseStatus.expired') || 'Expired', value: true }] }"
      :use-default-sort="false"
      :search-placeholder="globalTranslate('searchExtendedCourses')"
      :can-row-toggle="false"
      @selection-change="onSelectionChange"
    />

    <!-- Snackbar for notifications -->
    <v-snackbar
      v-model="snackbar.isShowing"
      location="top right"
      :timeout="5000"
    >
      <v-alert
        closable
        :type="snackbar.type"
        :color="snackbar.type"
        @click:close="snackbar.isShowing = false"
      >
        {{ snackbar.message }}
      </v-alert>
    </v-snackbar>
  </div>
</template>

<script setup>
import {
  ref, reactive, computed, onMounted,
} from 'vue';
import { eLearningService } from '@pfsc/core/services/e-learning.service';
import { useAuthStore } from '@pfsc/core/stores/auth.store';
import { appValidator } from '@pfsc/core/utils/validator';
import { useVuelidate } from '@vuelidate/core';
import moment from 'moment';
import { globalTranslate } from '@pfsc/shared/locale/app.i18n';
import { TABLE_FILTERS } from '../../common/shared/components/table-filters/table-filters-options';
import AppDatePicker from '../../common/shared/components/app-date-picker.vue';
import AssignedCoursesTable from '../../common/shared/components/assigned-courses/assigned-courses-table.vue';

const authStore = useAuthStore();

const assignedCoursesTableRef = ref(null);
const isUpdating = ref(false);
const currentPageIds = ref(new Set());

const persistentSelection = reactive({});
const bulkForm = reactive({
  expirationDate: null,
});

/** @type {import('vue').UnwrapRef<{isShowing: boolean, message: string, type: 'success' | 'error' | 'info' | 'warning'}>} */
const snackbar = reactive({
  isShowing: false,
  message: '',
  /** @type {'success' | 'error' | 'info' | 'warning'} */
  type: 'success',
});

const headers = [
  {
    title: 'User ID', key: 'user.id', sortable: true,
  },
  {
    title: 'Confirmation #', key: 'confirmationId', sortable: true,
  },
  {
    title: 'Order ID', key: 'orderId', sortable: false,
  },
  {
    title: 'Student', key: 'student', sortable: false,
  },
  {
    title: 'Course', key: 'courseName', sortable: false,
  },
  {
    title: 'Expiration Date', key: 'expiredDate', sortable: true, align: 'center',
  },
];

const rules = {
  bulkForm: {
    expirationDate: {
      required: appValidator.required,
      isValidDate: appValidator.validDate,
    },
  },
};
const v$ = useVuelidate(rules, { bulkForm });

const selectedCount = computed(() => Object.keys(persistentSelection).length);

const expirationDateErrors = computed(() => {
  const v = v$.value;
  const errors = v?.bulkForm?.expirationDate?.$errors;
  return errors ? errors.map(e => e.$message) : [];
});

/**
 * Notify message.
 * @param {string} message - Message to show.
 * @param {'success' | 'error' | 'info' | 'warning'} type - Type of notification.
 */
const openNotification = (message, type = 'success') => {
  snackbar.message = message;
  snackbar.type = type;
  snackbar.isShowing = true;
};

/**
 * Fetch extended assigned courses with initial date tracking.
 * @param {object} query Query params.
 * @returns {Promise<object>}
 */
const fetchExtendedAssignedCourses = async query => {
  const result = await eLearningService.getExtendedAssignedCourses(query);

  // Track current page IDs for selection management
  currentPageIds.value = new Set(result.data.map(item => item.id));

  result.data = result.data.map(item => ({
    ...item,
    initialExpiredDate: item.courseInfo?.expiredDate,
  }));
  return result;
};

/**
 * Handle selection changes from the table.
 * Merges/unmerges selections with the persistent selection map.
 * @param {object[]} currentPageSelection - Currently selected items on the current page.
 */
const onSelectionChange = currentPageSelection => {
  const currentPageSelectedIds = new Set(currentPageSelection.map(item => item.id));

  // Remove deselected items from persistent selection
  currentPageIds.value.forEach(id => {
    if (!currentPageSelectedIds.has(id)) {
      delete persistentSelection[id];
    }
  });

  // Add newly selected items to persistent selection
  currentPageSelection.forEach(item => {
    persistentSelection[item.id] = item;
  });
};

/**
 * Get all persistently selected courses.
 * @returns {object[]}
 */
const getAllSelectedCourses = () => Object.values(persistentSelection);

/**
 * Clear all persistent selections.
 */
const clearAllSelections = () => {
  Object.keys(persistentSelection).forEach(key => delete persistentSelection[key]);
};

/**
 * Handle bulk update.
 */
const onBulkUpdate = async () => {
  const v = v$.value;
  if (!v) return;

  v.$touch();
  if (v.$invalid) {
    return;
  }

  // Use persistent selection instead of current page selection
  const selectedCourses = getAllSelectedCourses();

  if (selectedCourses.length === 0) {
    openNotification('Please select at least one registration.', 'error');
    return;
  }

  // Frontend Validation: New Date > Old Date
  const newDate = moment(bulkForm.expirationDate);
  const invalidCourses = selectedCourses.filter(course => {
    const currentExp = moment(course.courseInfo.expiredDate);
    return newDate.isSameOrBefore(currentExp, 'day');
  });

  if (invalidCourses.length > 0) {
    const count = invalidCourses.length;
    const invalidIds = invalidCourses.map(c => c.confirmationId).join(', ');
    openNotification(
      `Cannot extend: ${count} selected registration(s) (Confirmation #: ${invalidIds}) have expiration date later than or equal to new date.`,
      'error',
    );
    return;
  }

  isUpdating.value = true;
  try {
    await eLearningService.batchExtend({
      registrationIds: selectedCourses.map(c => c.id),
      expirationDate: moment(bulkForm.expirationDate).format('YYYY-MM-DD'),
    });

    openNotification('Update successful for selected registrations.');

    // Refresh table while maintaining filters/search.
    const table = assignedCoursesTableRef.value;
    if (table) {
      await table.onUpdateStatusComplete();
    }
    clearAllSelections();
  } catch (e) {
    openNotification(e.response?.data?.message ?? e.message, 'error');
  } finally {
    isUpdating.value = false;
  }
};

onMounted(async () => {
  if (!authStore.currentUser) {
    await authStore.getProfile();
  }
});
</script>

<style module>
.bulkUpdateContent {
  position: relative;
  z-index: 0;
}

.bulkUpdateCard {
  margin-bottom: 20px;
  padding: 16px;
  overflow: visible;
  position: relative;
  z-index: 10;
}

.bulkUpdateWrapper {
  display: flex;
  align-items: center;
}

.datePickerWrapper {
  width: 250px;
  margin-right: 16px;
}

.bulkUpdateContent :global(.search-field) {
  min-width: 394px;
}
</style>
